import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { calcScoreBreakdown } from '@shared/score';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { roaInputStrToBps, roaPctToInputStr } from '@/lib/utils';
import type {
  CandidateWithDetails,
  CandidateProfile,
  CandidateOrigin,
  FunnelStatus,
  LossReason,
  CreateCandidateDTO,
  UpdateCandidateDTO,
} from '@/types';

const schema = z.object({
  name: z.string().min(2, 'Nome obrigatorio'),
  current_institution: z.string().min(1, 'Instituicao obrigatoria'),
  linkedin_url: z.string().url('URL inválida').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  tenure_years: z.coerce.number().min(0, 'Invalido'),
  profile: z.enum(['farmer', 'hunter', 'private', 'alta_renda']),
  aum_brl: z.coerce.number().min(0),
  roa_pct: z.coerce
    .number()
    .min(0, 'ROA invalido')
    .max(5, 'ROA deve ser entre 0 e 5%'),
  estimated_custody_brl: z.coerce.number().min(0),
  funnel_status: z.enum(['prospeccao', 'r1', 'r2_r3', 'proposta', 'fechado_ganho', 'fechado_perdido']),
  origin: z.enum(['linkedin', 'indicacao_interna', 'evento', 'ativo', 'headhunter']),
  onboarding_forecast: z.string().optional(),
  first_contact_date: z.string().min(1),
  entrepreneurship: z.number().min(1).max(5),
  execution: z.number().min(1).max(5),
  ambition: z.number().min(1).max(5),
  loss_reason: z
    .enum(['ficou_na_base', 'foi_concorrencia', 'perfil_nao_aderente', 'outro'])
    .optional()
    .nullable(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  candidate?: CandidateWithDetails | null;
  onSubmit: (data: CreateCandidateDTO | UpdateCandidateDTO) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

function scoreVariant(score: number) {
  if (score >= 70) return 'green';
  if (score >= 40) return 'yellow';
  return 'red';
}

const PROFILE_OPTIONS: { value: CandidateProfile; label: string }[] = [
  { value: 'farmer', label: 'Farmer' },
  { value: 'hunter', label: 'Hunter' },
  { value: 'private', label: 'Private' },
  { value: 'alta_renda', label: 'Alta Renda' },
];

const ORIGIN_OPTIONS: { value: CandidateOrigin; label: string }[] = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'indicacao_interna', label: 'Indicacao Interna' },
  { value: 'evento', label: 'Evento' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'headhunter', label: 'Headhunter' },
];

const STATUS_OPTIONS: { value: FunnelStatus; label: string }[] = [
  { value: 'prospeccao', label: 'Prospeccao' },
  { value: 'r1', label: 'R1 - Cafe' },
  { value: 'r2_r3', label: 'R2/R3 - Entrevista' },
  { value: 'proposta', label: 'Proposta' },
  { value: 'fechado_ganho', label: 'Fechado - Ganho' },
  { value: 'fechado_perdido', label: 'Fechado - Perdido' },
];

const LOSS_REASON_OPTIONS: { value: LossReason; label: string }[] = [
  { value: 'ficou_na_base', label: 'Ficou na base' },
  { value: 'foi_concorrencia', label: 'Foi para concorrencia' },
  { value: 'perfil_nao_aderente', label: 'Perfil nao aderente' },
  { value: 'outro', label: 'Outro' },
];

/* Layout helper — fluid 2-col grid that collapses to 1-col when the
   container is narrow (no viewport breakpoints needed). */
const fluidGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
  gap: '12px',
};

export function CandidateForm({ candidate, onSubmit, onCancel, isLoading }: Props) {
  const defaults: FormValues = {
    name: candidate?.name ?? '',
    current_institution: candidate?.current_institution ?? '',
    linkedin_url: candidate?.linkedin_url ?? '',
    phone: candidate?.phone ?? '',
    email: candidate?.email ?? '',
    tenure_years: candidate?.tenure_years ?? 0,
    profile: candidate?.profile ?? 'hunter',
    aum_brl: candidate ? Math.floor(candidate.aum_cents / 100) : 0,
    roa_pct: candidate ? parseFloat(roaPctToInputStr(candidate.roa_bps)) : 0,
    estimated_custody_brl: candidate
      ? Math.floor(candidate.estimated_custody_cents / 100)
      : 0,
    funnel_status: candidate?.funnel_status ?? 'prospeccao',
    origin: candidate?.origin ?? 'linkedin',
    onboarding_forecast: candidate?.onboarding_forecast ?? '',
    first_contact_date:
      candidate?.first_contact_date ?? format(new Date(), 'yyyy-MM-dd'),
    entrepreneurship: candidate?.qualitative_score?.entrepreneurship ?? 3,
    execution: candidate?.qualitative_score?.execution ?? 3,
    ambition: candidate?.qualitative_score?.ambition ?? 3,
    loss_reason: candidate?.loss_reason ?? null,
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const watchedValues = watch();
  const isFechadoPerdido = watchedValues.funnel_status === 'fechado_perdido';

  const scorePreview = calcScoreBreakdown({
    aum_cents: (watchedValues.aum_brl || 0) * 100,
    roa_bps: roaInputStrToBps(String(watchedValues.roa_pct || 0)),
    origin: watchedValues.origin,
    tenure_years: watchedValues.tenure_years || 0,
    qualitative: {
      entrepreneurship: watchedValues.entrepreneurship as 1 | 2 | 3 | 4 | 5,
      execution: watchedValues.execution as 1 | 2 | 3 | 4 | 5,
      ambition: watchedValues.ambition as 1 | 2 | 3 | 4 | 5,
    },
  });

  async function handleFormSubmit(values: FormValues) {
    const dto: CreateCandidateDTO = {
      name: values.name,
      current_institution: values.current_institution,
      tenure_years: values.tenure_years,
      profile: values.profile,
      aum_cents: Math.round(values.aum_brl * 100),
      roa_bps: roaInputStrToBps(String(values.roa_pct)),
      estimated_custody_cents: Math.round(values.estimated_custody_brl * 100),
      funnel_status: values.funnel_status,
      origin: values.origin,
      onboarding_forecast: values.onboarding_forecast || null,
      first_contact_date: values.first_contact_date,
      loss_reason: values.loss_reason ?? null,
      linkedin_url: values.linkedin_url || null,
      phone: values.phone || null,
      email: values.email || null,
      entrepreneurship: values.entrepreneurship as 1 | 2 | 3 | 4 | 5,
      execution: values.execution as 1 | 2 | 3 | 4 | 5,
      ambition: values.ambition as 1 | 2 | 3 | 4 | 5,
    };
    await onSubmit(dto);
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6">

      {/* ── Dados Basicos ─────────────────────────────────── */}
      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Dados Basicos
        </h3>

        {/* Nome — full width */}
        <div>
          <label className="text-xs text-text-secondary mb-1 block">Nome *</label>
          <Input {...register('name')} placeholder="Nome completo" />
          {errors.name && (
            <p className="text-xs text-status-red mt-0.5">{errors.name.message}</p>
          )}
        </div>

        {/* Instituicao + Tempo de casa — fluid 2-col */}
        <div style={fluidGrid}>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Instituicao *</label>
            <Input {...register('current_institution')} placeholder="XP, BTG, Rico..." />
            {errors.current_institution && (
              <p className="text-xs text-status-red mt-0.5">
                {errors.current_institution.message}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Tempo de casa (anos)</label>
            <Input {...register('tenure_years')} type="number" min="0" step="0.5" />
          </div>
        </div>

        {/* LinkedIn — full width */}
        <div>
          <label className="text-xs text-text-secondary mb-1 block">LinkedIn</label>
          <Input
            {...register('linkedin_url')}
            placeholder="https://linkedin.com/in/nome"
            type="url"
          />
          {errors.linkedin_url && (
            <p className="text-xs text-status-red mt-0.5">{errors.linkedin_url.message}</p>
          )}
        </div>

        {/* Telefone + Email — fluid 2-col */}
        <div style={fluidGrid}>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Telefone</label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                    let masked = digits;
                    if (digits.length > 2) masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                    if (digits.length > 7) masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                    field.onChange(masked);
                  }}
                  placeholder="(11) 99999-9999"
                  inputMode="tel"
                />
              )}
            />
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">E-mail</label>
            <Input
              {...register('email')}
              type="email"
              placeholder="nome@email.com"
            />
            {errors.email && (
              <p className="text-xs text-status-red mt-0.5">{errors.email.message}</p>
            )}
          </div>
        </div>

        {/* Perfil + Primeiro contato — fluid 2-col */}
        <div style={fluidGrid}>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Perfil</label>
            <Controller
              name="profile"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROFILE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Primeiro contato</label>
            <Input {...register('first_contact_date')} type="date" />
          </div>
        </div>
      </section>

      {/* ── Financeiro ────────────────────────────────────── */}
      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Financeiro
        </h3>

        {/* AUM + ROA — fluid 2-col */}
        <div style={fluidGrid}>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">AUM (R$)</label>
            <Input
              {...register('aum_brl')}
              type="number"
              min="0"
              placeholder="10000000"
            />
            {errors.aum_brl && (
              <p className="text-xs text-status-red mt-0.5">{errors.aum_brl.message}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">ROA (%)</label>
            <Input
              {...register('roa_pct')}
              type="number"
              min="0"
              max="5"
              step="0.01"
              placeholder="1.25"
            />
            {errors.roa_pct && (
              <p className="text-xs text-status-red mt-0.5">{errors.roa_pct.message}</p>
            )}
          </div>
        </div>

        {/* Custodia — full width */}
        <div>
          <label className="text-xs text-text-secondary mb-1 block">
            Custodia Estimada 12m (R$)
          </label>
          <Input
            {...register('estimated_custody_brl')}
            type="number"
            min="0"
            placeholder="0"
          />
        </div>
      </section>

      {/* ── Funil ─────────────────────────────────────────── */}
      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Funil
        </h3>

        {/* Status + Origem — fluid 2-col */}
        <div style={fluidGrid}>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Status</label>
            <Controller
              name="funnel_status"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Origem</label>
            <Controller
              name="origin"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORIGIN_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Onboarding — full width */}
        <div>
          <label className="text-xs text-text-secondary mb-1 block">
            Previsao de Onboarding (AAAA-MM)
          </label>
          <Input
            {...register('onboarding_forecast')}
            placeholder="2026-06"
            pattern="\d{4}-\d{2}"
          />
        </div>
      </section>

      {/* ── Avaliacao Qualitativa ─────────────────────────── */}
      <section className="flex flex-col gap-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Avaliacao Qualitativa
        </h3>
        {(
          [
            { name: 'entrepreneurship', label: 'Empreendedorismo' },
            { name: 'execution', label: 'Execucao' },
            { name: 'ambition', label: 'Ambicao' },
          ] as const
        ).map(({ name, label }) => (
          <div key={name} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-xs text-text-secondary">{label}</label>
              <span className="text-xs font-mono text-text-primary">
                {watchedValues[name]}/5
              </span>
            </div>
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <Slider
                  min={1}
                  max={5}
                  step={1}
                  value={[field.value]}
                  onValueChange={([v]) => field.onChange(v)}
                />
              )}
            />
          </div>
        ))}
      </section>

      {/* ── Motivo da Perda ───────────────────────────────── */}
      {isFechadoPerdido && (
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Motivo da Perda
          </h3>
          <Controller
            name="loss_reason"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onValueChange={(v) => field.onChange(v || null)}
              >
                <SelectTrigger><SelectValue placeholder="Selecionar motivo" /></SelectTrigger>
                <SelectContent>
                  {LOSS_REASON_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </section>
      )}

      {/* ── Score Preview ─────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-bg-base p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-muted">Score estimado</span>
          <Badge variant={scoreVariant(scorePreview.total)} className="text-sm font-mono font-bold px-2 py-1">
            {scorePreview.total}
          </Badge>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px' }}>
          {[
            { label: 'AUM', value: scorePreview.aum_points, max: 30 },
            { label: 'ROA', value: scorePreview.roa_points, max: 20 },
            { label: 'Orig', value: scorePreview.origin_bonus, max: 10 },
            { label: 'Casa', value: scorePreview.tenure_points, max: 15 },
            { label: 'Qual', value: scorePreview.qualitative_points, max: 25 },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center gap-0.5">
              <span className="text-xs font-mono text-text-primary">{item.value}</span>
              <div className="w-full h-1 rounded-full bg-bg-elevated overflow-hidden">
                <div
                  className="h-full bg-accent-indigo rounded-full"
                  style={{ width: `${(item.value / item.max) * 100}%` }}
                />
              </div>
              <span className="text-xs text-text-muted">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────── */}
      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Salvando...' : candidate ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </div>
    </form>
  );
}
